import React, { useState, useEffect, useRef } from 'react';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LabelList,
} from 'recharts';
import { Eye, Flame, TrendingUp, Play, BarChart3, Instagram, Youtube, Heart, CalendarDays, Clapperboard, MessageCircle } from 'lucide-react';
import { GlassCard } from '../_shared/GlassCard';
import { Footer } from '../_shared/Footer';

/* ----------------------------------------------------------------------------
   DATA — July 2026, scraped LIVE on 2026-09-07 (counts settled, 5+ weeks old).
   Instagram: Apify instagram-scraper, full month, ALL post types (30 posts, all reels).
     Raw: clients-current/kush/research/kush-ig-july-full-2026-09-07.json
   YouTube: yt-dlp public view counts (30 shorts).
     Raw: clients-current/kush/research/kush-youtube-shorts-2026-09-07.txt
   Plays are public play counts, NOT private reach.
---------------------------------------------------------------------------- */

const IG = { posts: 30, plays: 2679413, likes: 108985, comments: 10676, avg: 89313, median: 5073 };
const YT = { posts: 30, views: 89936, avg: 2997, best: 15896 };
const TOTAL_VIDEOS = IG.posts + YT.posts;

/* Per-day Instagram plays (day of July). Jul 16 + 18 had no IG post; Jul 17 had two. */
const DAILY = [
    { day: 1, plays: 2901, hook: 'Free open-source clipping tool' },
    { day: 2, plays: 7044, hook: 'Data privacy for Indian businesses' },
    { day: 3, plays: 5726, hook: 'AMD computer that fits in your hand' },
    { day: 4, plays: 2729, hook: 'Free desktop AI from a Chinese lab' },
    { day: 5, plays: 1668, hook: 'ChatGPT is more than a search engine' },
    { day: 6, plays: 3148, hook: 'US government inside an AI company' },
    { day: 7, plays: 5073, hook: "Vietnam's 1,000 electric launches" },
    { day: 8, plays: 8170, hook: 'GLM-5.2 open-sourced' },
    { day: 9, plays: 20665, hook: 'Kronos, the forecasting AI' },
    { day: 10, plays: 1087331, hook: 'AI data centers vs your drinking water' },
    { day: 11, plays: 2781, hook: 'Chrome extension that disguises AI' },
    { day: 12, plays: 4692, hook: '$3M brand in 30 days' },
    { day: 13, plays: 5362, hook: "See every competitor's ad" },
    { day: 14, plays: 95962, hook: 'Get paid every time AI waits' },
    { day: 15, plays: 4447, hook: 'Faceless YouTube channels' },
    { day: 16, plays: 0, hook: 'No Instagram post' },
    { day: 17, plays: 1078969, hook: '13,000 AI agents opened wallets (+1 more reel)' },
    { day: 18, plays: 0, hook: 'No Instagram post' },
    { day: 19, plays: 283492, hook: 'Student out-reported the news channels' },
    { day: 20, plays: 3210, hook: 'Still building the hard way?' },
    { day: 21, plays: 3090, hook: '24/7 AI agent, no terminal' },
    { day: 22, plays: 2982, hook: '24/7 AI agent, part two' },
    { day: 23, plays: 2875, hook: 'AI applies to jobs while you sleep' },
    { day: 24, plays: 2542, hook: 'Marketing stack in one chat window' },
    { day: 25, plays: 8337, hook: 'Startup launch loop is a solo job' },
    { day: 26, plays: 4224, hook: 'Top AI lab re-priced for one country' },
    { day: 27, plays: 10549, hook: 'You have a lead problem' },
    { day: 28, plays: 6890, hook: "Wikipedia's human-writing guide" },
    { day: 29, plays: 2808, hook: 'Cytation partnership' },
    { day: 30, plays: 8109, hook: 'Bloomberg Terminal for free' },
    { day: 31, plays: 3637, hook: 'Drone shots without a drone' },
];

/* Posting cadence: which days each platform published. */
const IG_DAYS = new Set([1,2,3,4,5,6,7,8,9,10,11,12,13,14,15,17,19,20,21,22,23,24,25,26,27,28,29,30,31]);
const YT_DAYS = new Set([1,2,3,4,5,6,7,8,9,10,11,12,13,14,15,17,18,19,20,21,22,23,24,25,26,27,28,29,30,31]);
const IG_DOUBLE = new Set([17]);

const IG_TOP = [
    { hook: 'AI data centers vs your drinking water', plays: '1,087,331', likes: '56,867', comments: '867', url: 'https://www.instagram.com/p/DanPzHkhFys/', gradient: 'from-blue-600 via-indigo-500 to-violet-500', date: 'Jul 10' },
    { hook: '13,000 AI agents opened wallets to survive', plays: '1,072,682', likes: '29,819', comments: '6,595', url: 'https://www.instagram.com/p/Da4XlSfhtNo/', gradient: 'from-emerald-600 via-teal-500 to-cyan-400', date: 'Jul 17' },
    { hook: 'The college student who out-reported the news channels', plays: '283,492', likes: '19,135', comments: '362', url: 'https://www.instagram.com/p/Da-rhw6NJBL/', gradient: 'from-yellow-600 via-amber-500 to-orange-400', date: 'Jul 19' },
    { hook: 'Get paid every time Claude or ChatGPT waits', plays: '95,962', likes: '1,354', comments: '1,900', url: 'https://www.instagram.com/p/DaxrltihiEM/', gradient: 'from-purple-600 via-fuchsia-500 to-pink-500', date: 'Jul 14' },
    { hook: 'Kronos, the open-source forecasting AI', plays: '20,665', likes: '482', comments: '229', url: 'https://www.instagram.com/p/Dakr5pIhMI4/', gradient: 'from-red-600 via-rose-500 to-orange-400', date: 'Jul 9' },
];

const YT_TOP = [
    { hook: 'The AI that dies if it does not make money', views: '15,896', url: 'https://www.youtube.com/shorts/zL2GqJp_Tgw', gradient: 'from-emerald-600 via-teal-500 to-cyan-400', date: 'Jul 17' },
    { hook: 'Launch a real startup without hiring anyone', views: '13,251', url: 'https://www.youtube.com/shorts/SqzKveZMd5E', gradient: 'from-yellow-600 via-amber-500 to-orange-400', date: 'Jul 25' },
    { hook: "Jeff Bezos: humans are drinking AI's water", views: '9,505', url: 'https://www.youtube.com/shorts/hFSaY2JcrZU', gradient: 'from-blue-600 via-indigo-500 to-violet-500', date: 'Jul 10' },
    { hook: 'An AI agent that runs 24/7, no terminal', views: '6,581', url: 'https://www.youtube.com/shorts/29n-FkVN16c', gradient: 'from-purple-600 via-fuchsia-500 to-pink-500', date: 'Jul 21' },
    { hook: 'Get paid every time Claude makes you wait', views: '4,871', url: 'https://www.youtube.com/shorts/LHllztLL05Q', gradient: 'from-red-600 via-rose-500 to-orange-400', date: 'Jul 14' },
];

/* ---------------------------------------------------------------- count-up hook */
const useCounter = (end, duration = 1400) => {
    const [count, setCount] = useState(0);
    const ref = useRef(null);
    const hasAnimated = useRef(false);
    useEffect(() => {
        const reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting && !hasAnimated.current) {
                    hasAnimated.current = true;
                    if (reduced) { setCount(end); return; }
                    const start = performance.now();
                    const animate = (now) => {
                        const progress = Math.min(1, (now - start) / duration);
                        const eased = 1 - Math.pow(1 - progress, 3);
                        setCount(Math.floor(end * eased));
                        if (progress < 1) requestAnimationFrame(animate);
                    };
                    requestAnimationFrame(animate);
                }
            },
            { threshold: 0.3 }
        );
        if (ref.current) observer.observe(ref.current);
        return () => observer.disconnect();
    }, [end, duration]);
    return [count, ref];
};

const KPICard = ({ icon: Icon, iconColor, label, value, subtext }) => {
    const [animatedValue, ref] = useCounter(value);
    return (
        <GlassCard className="flex flex-col justify-between min-h-[170px]">
            <div ref={ref}>
                <div className={`${iconColor} p-3 rounded-xl mb-4 inline-flex`}>
                    <Icon size={22} />
                </div>
                <p className="text-white/50 text-xs uppercase tracking-widest font-semibold mb-2">{label}</p>
                <p className="text-4xl font-black text-white tracking-tight tabular-nums">
                    {animatedValue.toLocaleString()}
                </p>
                <p className="text-white/50 text-sm mt-3">{subtext}</p>
            </div>
        </GlassCard>
    );
};

const PlatformLabel = ({ icon: Icon, color, name, tagline }) => (
    <div className="text-center mb-10">
        <div className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full border ${color.border} ${color.bg} backdrop-blur-md mb-4`}>
            <Icon size={15} className={color.text} />
            <span className={`text-xs uppercase tracking-widest font-semibold ${color.text}`}>{name}</span>
        </div>
        {tagline && <p className="text-white/50 max-w-2xl mx-auto">{tagline}</p>}
    </div>
);

/* ----------------------------------------------------------------- Hero section */
const Hero = () => (
    <section className="min-h-[80vh] flex flex-col justify-center items-center text-center px-4 pt-24 pb-12 relative z-10">
        <div className="space-y-6 max-w-5xl mx-auto w-full">
            <div className="inline-flex items-center justify-center px-4 py-1.5 rounded-full border border-yellow-500/30 bg-yellow-500/10 backdrop-blur-md mb-2">
                <span className="text-sm font-medium text-yellow-300 tracking-wide uppercase">July 2026 · Monthly Report · @realkushdesai</span>
            </div>

            <h1 className="text-4xl md:text-6xl lg:text-7xl font-black tracking-tighter text-transparent bg-clip-text bg-gradient-to-r from-white via-yellow-100 to-white drop-shadow-lg leading-tight">
                Two Videos a Day.<br />Two Million-Play Reels.
            </h1>

            <p className="text-lg md:text-xl text-yellow-200/70 max-w-2xl mx-auto leading-relaxed">
                <span className="text-white font-semibold">60 videos</span> shipped in July: a reel on Instagram and a short on YouTube, every day. Instagram pulled <span className="text-white font-semibold">2.68 million plays</span>, and two reels crossed a million each.
            </p>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mt-12 w-full">
                <KPICard icon={Clapperboard} iconColor="bg-amber-500/20 text-amber-300" label="Videos Published" value={TOTAL_VIDEOS} subtext="30 reels + 30 shorts" />
                <KPICard icon={Eye} iconColor="bg-rose-500/20 text-rose-300" label="Instagram Plays" value={IG.plays} subtext={`${IG.avg.toLocaleString()} avg per reel`} />
                <KPICard icon={Heart} iconColor="bg-pink-500/20 text-pink-300" label="Instagram Likes" value={IG.likes} subtext={`+ ${IG.comments.toLocaleString()} comments`} />
                <KPICard icon={Youtube} iconColor="bg-red-500/20 text-red-300" label="YouTube Views" value={YT.views} subtext={`${YT.avg.toLocaleString()} avg per short`} />
            </div>

            <p className="text-white/30 text-xs max-w-2xl mx-auto pt-6">
                Scraped live on Sep 7, 2026, five weeks after July ended, so every count is settled. Instagram figures are public play counts, not private reach. YouTube figures are public view counts.
            </p>
        </div>
    </section>
);

/* ------------------------------------------------------ Posting cadence strip */
const CadenceCell = ({ posted, double }) => (
    <div
        className={`h-5 w-full rounded-[4px] ${posted ? (double ? 'bg-yellow-300' : 'bg-yellow-400/80') : 'bg-white/[0.06] border border-white/10'}`}
        title={posted ? (double ? '2 posts' : '1 post') : 'No post'}
    />
);

const Cadence = () => (
    <section className="py-16 px-4">
        <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-10">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-white/10 mb-4">
                    <CalendarDays size={14} className="text-yellow-300" />
                    <span className="text-xs uppercase tracking-widest text-white/60 font-semibold">Publishing Cadence · All 31 Days</span>
                </div>
                <h2 className="text-3xl md:text-5xl font-bold text-white mb-3">The Two-a-Day Machine</h2>
                <p className="text-white/50 max-w-2xl mx-auto">
                    Every filled block is a published video. Instagram rested on Jul 16 and 18 and dropped a double on Jul 17. YouTube missed only Jul 16. Everything else shipped on schedule.
                </p>
            </div>

            <GlassCard>
                <div className="overflow-x-auto">
                    <div className="min-w-[640px]">
                        <div className="grid gap-1 mb-1" style={{ gridTemplateColumns: '90px repeat(31, 1fr)' }}>
                            <div />
                            {DAILY.map((d) => (
                                <div key={d.day} className="text-center text-[9px] text-white/40 font-mono">{d.day}</div>
                            ))}
                        </div>
                        <div className="grid gap-1 mb-2 items-center" style={{ gridTemplateColumns: '90px repeat(31, 1fr)' }}>
                            <div className="flex items-center gap-1.5 text-xs text-rose-300 font-semibold pr-2"><Instagram size={12} /> Instagram</div>
                            {DAILY.map((d) => (
                                <CadenceCell key={d.day} posted={IG_DAYS.has(d.day)} double={IG_DOUBLE.has(d.day)} />
                            ))}
                        </div>
                        <div className="grid gap-1 items-center" style={{ gridTemplateColumns: '90px repeat(31, 1fr)' }}>
                            <div className="flex items-center gap-1.5 text-xs text-red-300 font-semibold pr-2"><Youtube size={12} /> YouTube</div>
                            {DAILY.map((d) => (
                                <CadenceCell key={d.day} posted={YT_DAYS.has(d.day)} />
                            ))}
                        </div>
                    </div>
                </div>
                <div className="flex items-center justify-between mt-5 pt-4 border-t border-white/5">
                    <div className="flex items-center gap-4 text-xs text-white/40">
                        <span className="flex items-center gap-1.5"><span className="inline-block h-3 w-3 rounded-[3px] bg-yellow-400/80" /> published</span>
                        <span className="flex items-center gap-1.5"><span className="inline-block h-3 w-3 rounded-[3px] bg-yellow-300" /> double drop</span>
                        <span className="flex items-center gap-1.5"><span className="inline-block h-3 w-3 rounded-[3px] bg-white/[0.06] border border-white/10" /> rest</span>
                    </div>
                    <p className="text-white/60 text-sm font-semibold tabular-nums">60 videos / 31 days</p>
                </div>
            </GlassCard>
        </div>
    </section>
);

/* ------------------------------------------------------ Daily plays chart */
const fmtK = (n) => (n >= 1000000 ? `${(n / 1000000).toFixed(1)}M` : n >= 1000 ? `${Math.round(n / 1000)}K` : `${n}`);

const ChartTooltip = ({ active, payload }) => {
    if (!active || !payload || !payload.length) return null;
    const d = payload[0].payload;
    return (
        <div className="bg-black/85 backdrop-blur-md border border-white/15 rounded-xl px-4 py-3 text-left max-w-[240px]">
            <p className="text-white font-bold text-sm mb-1">July {d.day}</p>
            <p className="text-yellow-300 text-sm tabular-nums">{d.plays.toLocaleString()} plays</p>
            <p className="text-white/50 text-xs mt-1 leading-snug">{d.hook}</p>
        </div>
    );
};

const spikeLabel = (props) => {
    const { x, y, width, value } = props;
    if (value < 100000) return null;
    return (
        <text x={x + width / 2} y={y - 8} fill="#fde68a" fontSize={11} fontWeight={700} textAnchor="middle">
            {fmtK(value)}
        </text>
    );
};

const DailyChart = () => (
    <section className="py-4 px-4">
        <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-10">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-white/10 mb-4">
                    <BarChart3 size={14} className="text-yellow-300" />
                    <span className="text-xs uppercase tracking-widest text-white/60 font-semibold">Instagram Plays · Day by Day</span>
                </div>
                <h2 className="text-3xl md:text-5xl font-bold text-white mb-3">When July Spiked</h2>
                <p className="text-white/50 max-w-2xl mx-auto">
                    The daily baseline hums in the thousands, then three story reels detonate: Jul 10, Jul 17 and Jul 19. Hover any bar for the reel behind it.
                </p>
            </div>

            <GlassCard>
                <div className="h-[360px] w-full min-h-[360px]">
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={DAILY} margin={{ top: 28, right: 10, left: -6, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" vertical={false} />
                            <XAxis dataKey="day" tick={{ fill: 'rgba(255,255,255,0.45)', fontSize: 10 }} axisLine={false} tickLine={false} interval={1} />
                            <YAxis tickFormatter={fmtK} tick={{ fill: 'rgba(255,255,255,0.45)', fontSize: 11 }} axisLine={false} tickLine={false} width={46} />
                            <Tooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(255,255,255,0.05)' }} />
                            <Bar dataKey="plays" fill="#fbbf24" radius={[4, 4, 0, 0]} minPointSize={2}>
                                <LabelList dataKey="plays" content={spikeLabel} />
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </div>
                <p className="text-white/30 text-xs text-center mt-4">
                    Public play counts per posting day. Jul 17 combines two reels (1,072,682 + 6,287). Jul 16 and 18 had no Instagram post.
                </p>
            </GlassCard>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-6">
                <GlassCard className="text-center">
                    <p className="text-3xl font-black text-yellow-300 mb-2 tabular-nums">2 × 1M+</p>
                    <p className="text-white/70 text-sm leading-relaxed">Two reels crossed a million plays in the same month: the water story (1.09M) and the Automaton story (1.07M).</p>
                </GlassCard>
                <GlassCard className="text-center">
                    <p className="text-3xl font-black text-yellow-300 mb-2 tabular-nums">89,313</p>
                    <p className="text-white/70 text-sm leading-relaxed">Average plays per reel across all 30. The median reel did 5,073, the breakouts did the heavy lifting.</p>
                </GlassCard>
                <GlassCard className="text-center">
                    <p className="text-3xl font-black text-yellow-300 mb-2 tabular-nums">6,595</p>
                    <p className="text-white/70 text-sm leading-relaxed">Comments on the Automaton reel alone, out of 10,676 across the month. The conversation engine is on.</p>
                </GlassCard>
            </div>
        </div>
    </section>
);

/* ------------------------------------------------------------ reel/video tiles */
const ReelTile = ({ reel, rank, footerSub }) => (
    <a
        href={reel.url}
        target="_blank"
        rel="noopener noreferrer"
        className="group relative block rounded-[2rem] overflow-hidden transition-transform duration-300 hover:scale-[1.03] cursor-pointer"
        style={{ aspectRatio: '9/16' }}
    >
        <div className={`absolute inset-0 bg-gradient-to-br ${reel.gradient}`} />
        <span className="absolute -bottom-6 -right-2 text-[9rem] font-black text-white/10 leading-none select-none">{rank}</span>
        <div className="absolute top-3 left-3">
            <span className="bg-black/40 backdrop-blur-md text-white text-xs font-bold px-2.5 py-1 rounded-full border border-white/10">#{rank}</span>
        </div>
        <div className="absolute top-3 right-3">
            <span className="bg-black/40 backdrop-blur-md text-white/70 text-[10px] font-semibold px-2 py-1 rounded-full border border-white/10">{reel.date}</span>
        </div>
        <div className="absolute top-1/3 left-3 right-3">
            <div className="bg-black/60 backdrop-blur-sm rounded-xl px-3 py-2 border border-white/10">
                <p className="text-white text-xs sm:text-sm font-bold leading-tight text-center">{reel.hook}</p>
            </div>
        </div>
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 group-hover:backdrop-blur-sm transition-all duration-300 flex items-center justify-center">
            <div className="opacity-0 group-hover:opacity-100 transition-all duration-300 transform scale-75 group-hover:scale-100">
                <div className="w-14 h-14 rounded-full bg-white/20 backdrop-blur-md border border-white/30 flex items-center justify-center">
                    <Play size={24} className="text-white ml-1" fill="white" />
                </div>
            </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/80 to-transparent">
            <p className="text-white text-sm font-bold tabular-nums">{reel.plays || reel.views}</p>
            {footerSub && <p className="text-white/50 text-[11px] font-mono">{footerSub(reel)}</p>}
        </div>
    </a>
);

const IGTopReels = () => (
    <section className="py-16 px-4">
        <div className="container mx-auto max-w-6xl">
            <PlatformLabel
                icon={Instagram}
                color={{ border: 'border-rose-500/30', bg: 'bg-rose-500/10', text: 'text-rose-300' }}
                name="Instagram · Top 5 Reels"
                tagline="Tap any card to watch. Play counts above, likes and comments below."
            />
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
                {IG_TOP.map((reel, i) => <ReelTile key={i} reel={reel} rank={i + 1} footerSub={(r) => `${r.likes} likes · ${r.comments} comments`} />)}
            </div>
        </div>
    </section>
);

/* ----------------------------------------------------------- YouTube section */
const YouTubeSection = () => (
    <section className="py-8 px-4">
        <div className="container mx-auto max-w-6xl">
            <PlatformLabel
                icon={Youtube}
                color={{ border: 'border-red-500/30', bg: 'bg-red-500/10', text: 'text-red-300' }}
                name="YouTube · July Shorts"
                tagline="The second daily video: 30 shorts in 31 days. The same stories won on both platforms, Automaton took #1 on each."
            />

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mb-12">
                <KPICard icon={Play} iconColor="bg-red-500/20 text-red-300" label="Shorts Posted" value={YT.posts} subtext="One a day in July" />
                <KPICard icon={Eye} iconColor="bg-blue-500/20 text-blue-300" label="Total Views" value={YT.views} subtext={`${YT.avg.toLocaleString()} avg per short`} />
                <KPICard icon={TrendingUp} iconColor="bg-emerald-500/20 text-emerald-300" label="Best Short" value={YT.best} subtext="The Automaton story, again" />
                <KPICard icon={MessageCircle} iconColor="bg-amber-500/20 text-amber-300" label="Cross-Platform Wins" value={3} subtext="Stories in both top 5s" />
            </div>

            <h3 className="text-2xl md:text-3xl font-bold text-white mb-3 text-center">Top 5 Shorts</h3>
            <p className="text-white/50 text-center mb-10 max-w-xl mx-auto">Tap any card to watch.</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
                {YT_TOP.map((reel, i) => <ReelTile key={i} reel={reel} rank={i + 1} />)}
            </div>
        </div>
    </section>
);

/* ----------------------------------------------------------- Learnings */
const Learnings = () => {
    const items = [
        {
            title: 'Stories with stakes win',
            body: 'Both million-play reels are stories about the world: data centers competing with your tap water, and AI agents that must earn or die. Straight tool announcements stayed in the low thousands. Every new script now starts from a story, never from a feature list.',
        },
        {
            title: 'India + real conflict multiplies',
            body: 'The two India reels with a genuine tension (water, journalism) took two of the top three spots. An India angle without conflict did nothing. We keep the flag only when there is a fight in the story.',
        },
        {
            title: 'The comment engine works',
            body: 'The Automaton reel pulled 6,595 comments. On the "get paid when AI waits" reel, comments outran likes: 1,900 against 1,354. The comment CTA turns viewers into conversations, and conversations into reach.',
        },
    ];
    return (
        <section className="py-16 px-4">
            <div className="container mx-auto max-w-5xl">
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-3 text-center">What July Taught Us</h2>
                <p className="text-white/50 text-center mb-10 max-w-2xl mx-auto">Every reel is scored after posting and the lessons feed straight into the next batch of scripts.</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {items.map((it, i) => (
                        <GlassCard key={i}>
                            <div className="flex items-center gap-2 mb-3">
                                <Flame size={16} className="text-yellow-300" />
                                <p className="text-lg font-bold text-white">{it.title}</p>
                            </div>
                            <p className="text-white/70 text-sm leading-relaxed">{it.body}</p>
                        </GlassCard>
                    ))}
                </div>
            </div>
        </section>
    );
};

/* ----------------------------------------------------------------- Page shell */
export default function KushReport8() {
    useEffect(() => {
        const pageTitle = 'Kush July 2026 Monthly Report | SocialSEO';
        document.title = pageTitle;
        const ensureMeta = (selector, attributes) => {
            let el = document.querySelector(selector);
            if (!el) {
                el = document.createElement('meta');
                Object.entries(attributes).forEach(([k, v]) => el.setAttribute(k, v));
                document.head.appendChild(el);
            }
            el.setAttribute('content', pageTitle);
        };
        ensureMeta('meta[property="og:title"]', { property: 'og:title' });
        ensureMeta('meta[name="twitter:title"]', { name: 'twitter:title' });
    }, []);

    return (
        <div className="font-sans leading-relaxed text-white relative" style={{ fontFamily: "'Plus Jakarta Sans', Inter, sans-serif" }}>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800&display=swap');
            `}</style>

            <header className="fixed top-0 left-0 right-0 z-50 p-4 md:p-6 flex justify-between items-center bg-transparent pointer-events-none">
                <div className="bg-white/10 backdrop-blur-lg border border-white/10 px-4 py-2 rounded-full pointer-events-auto">
                    <span className="font-bold tracking-tight text-yellow-300">
                        <span className="text-white">Social</span>SEO
                    </span>
                </div>
                <div className="bg-white/10 backdrop-blur-lg border border-white/10 px-4 py-2 rounded-full pointer-events-auto">
                    <span className="text-sm font-medium text-white/80">July 2026</span>
                </div>
            </header>

            <div className="relative z-10">
                <main>
                    <Hero />
                    <Cadence />
                    <DailyChart />
                    <IGTopReels />
                    <YouTubeSection />
                    <Learnings />
                </main>
                <Footer />
            </div>
        </div>
    );
}
